import type { DeadlineNoticeAnchor } from './customerDeadlines';
import type { TimelineEntry } from './api/domains/projects';
import { markNetworkError } from './api/http';
import { useCustomerStore } from '../stores/customerStore';
/**
 * API-Client für das Kunden-Portal.
 * Separater Namespace von der Techniker-API, eigene Endpunkte unter /api/customer/*.
 */
import type {
  CustomerDeadlineBase,
  CustomerDeadlineCategory,
  CustomerDeadlineOwnership,
  CustomerDeadlineStatus,
  DeadlineProof,
} from './customerDeadlines';

// ─── Typen ────────────────────────────────────────────────────────────────────

export interface PortalNotice {
  id: string;
  type: 'STOERUNG' | 'WARTUNG' | 'INFO';
  title: string;
  message: string;
  validUntil: string | null;
}

export interface PortalLocationOption {
  key: string;
  label: string;
  cwSiteId: number;
}

/** Portal-Bereiche, die je Kunde freigeschaltet werden (Register: backend/constants/portalModules.ts) */
export type PortalModuleId =
  | 'tickets'
  | 'requests'
  | 'inbox'
  | 'shop'
  | 'quotes'
  | 'submissions'
  | 'reports'
  | 'assets'
  | 'contracts'
  | 'appointments'
  | 'projects'
  | 'trust'
  | 'audit'
  | 'dns';

export interface CustomerContact {
  id: string;
  email: string | null;
  name: string | null;
  phone: string | null;
  role: 'ADMIN' | 'MITARBEITER' | 'GAST';
  /** true = Ansprechpartner-Session; false = Firmen-Session ohne Kontakt */
  hasContact: boolean;
  cwCompanyId: number | null;
  weclappCustomerId: string | null;
  /** CSM-Vorabkontakt erledigt – Freigabe für Welcome-Mail (Phase 2.10). */
  csmPreContactCompletedAt?: string | null;
  /** Stammdaten für Standort-Filter (Phase 2.11). */
  portalLocationOptions?: PortalLocationOption[] | null;
  locationFilterAvailable?: boolean;
  onboarding: {
    variant: 'NEW_CUSTOMER' | 'EXISTING_CUSTOMER';
    startedAt: string;
    tourCompletedAt: string | null;
    checklistState: Record<string, boolean> | null;
    checklistItems?: Array<{ id: string; label: string }> | null;
    /** Bestandskunden: Einmal-Banner „Portal-Neu” (Phase 6.3) */
    existingLaunchBannerDismissed?: boolean;
  } | null;
  /** Aktive globale Portal-Meldungen (inkl. kunden-spezifischer Tags) */
  portalNotices?: PortalNotice[];
  /** Öffentlicher Status / Störungsbanner (Phase 4.6), mit /me geliefert — Compat-Feld */
  portalStatus?: {
    degraded: boolean;
    message: string | null;
    updatedAt: string;
  };
  /** Firmenname des Kunden (nur bei Kontakt-Sessions) */
  companyName?: string | null;
  /** Konfiguration für Firmen-Sessions (nur wenn hasContact=false) */
  companySessionConfig?: {
    showQuotes: boolean;
  };
  /**
   * Für diesen Kunden freigegebene Portal-Bereiche. Alles andere steht mit
   * „Demnächst" in der Navigation. Fehlt das Feld (alte Sitzung im localStorage),
   * gilt bis zum nächsten /me nur die Fristenübersicht.
   */
  portalModules?: PortalModuleId[];
  /**
   * Was die aktive Gesellschaft freigibt, ohne persönliche Einschränkung. Steht ein Bereich hier,
   * aber nicht in `portalModules`, ist er für diese Person abgeschaltet → „Kein Zugriff" statt „Demnächst".
   */
  companyModules?: PortalModuleId[];
  /** Rechte dieses Ansprechpartners (Rolle + Pakete, serverseitig aufgelöst) */
  permissions?: string[];
  /** Ticket-Sichtmodus — „ALL" öffnet die firmenweiten Berichte */
  ticketViewMode?: 'OWN' | 'INCLUDE' | 'EXCLUDE' | 'ALL';
  /**
   * Gesellschaften des Konzerns, die dieser Ansprechpartner sehen darf. Ein Eintrag =
   * kein Konzern-Zugriff, dann bleibt der Umschalter weg.
   */
  companies?: PortalCompany[];
  /** Gerade aktive Gesellschaft — alle Daten im Portal beziehen sich darauf. */
  activeCustomerId?: string;
  /**
   * Mitarbeiter-Ansicht: gesetzt, wenn ein FixByte-Techniker das Portal in der Sicht
   * dieses Kontakts geöffnet hat. Die Sitzung ist nur lesend und endet zu `expiresAt`.
   */
  preview?: {
    byName: string;
    expiresAt: string;
    /** false = Kontakt hat selbst noch keinen Zugang — die Ansicht zeigt den Stand nach Freischaltung. */
    contactPortalEnabled: boolean;
    customerPortalEnabled: boolean;
    /** Angenommene Rolle („Vorschau mit dieser Auswahl") — null = die echten Rechte des Kontakts. */
    assumedRole?: string | null;
  } | null;
}

export interface PortalCompany {
  id: string;
  name: string;
  customerNumber?: string | null;
  /** true = die eigene Gesellschaft des Kontakts */
  isHome: boolean;
}

export interface CustomerSession {
  id: string;
  deviceLabel: string | null;
  createdAt: string;
  lastSeenAt: string;
  isCurrent: boolean;
}

export type TicketStatus = 'open' | 'in_progress' | 'waiting' | 'closed';
export type TicketPriority = 'critical' | 'high' | 'medium' | 'low';

export interface CustomerTicket {
  id: string;
  source: 'cw' | 'weclapp';
  number: string;
  title: string;
  status: TicketStatus;
  statusLabel: string;
  priority: TicketPriority;
  priorityLabel: string;
  assignedTo: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** ConnectWise Standort (bei „Alle Standorte” hilfreich) */
  siteName?: string | null;
  /** Fälligkeitsdatum / Deadline (ISO-String oder null) */
  deadline?: string | null;
}

export interface CustomerTicketDetail extends CustomerTicket {
  description: string | null;
  board: string | null;
  /** ConnectWise: Typ · Untertyp · Item */
  categoryLabel?: string | null;
  /** ConnectWise Standortname */
  siteName?: string | null;
  /** ConnectWise Ziel-/Fälligkeitsdatum (ISO) */
  requiredDate?: string | null;
  /** Name des Ticket-Erstellers / Melders */
  createdByName?: string | null;
  /** Ob der Kunde Dateien an dieses Ticket anhängen darf (nur CW). */
  canUploadAttachments?: boolean;
  /** Ob der Kunde den Titel bearbeiten darf (ADMIN: alle, sonst nur eigene). */
  canEditTitle?: boolean;
}

export interface CustomerTicketAttachmentMeta {
  id: number;
  name: string;
  sizeBytes: number | null;
  createdAt: string | null;
  /** Relativer Pfad unter gleicher Origin */
  downloadUrl: string;
}

export interface CustomerSubmission {
  id: string;
  type: string;
  status: string;
  hasPdf: boolean;
  createdAt: string;
  updatedAt: string;
  customerName: string | null;
}

export interface CustomerAsset {
  id: string;
  source: 'cw' | 'weclapp';
  name: string;
  serialNumber: string | null;
  model: string | null;
  manufacturer: string | null;
  type: string | null;
  status: string | null;
  warrantyExpiry: string | null;
  purchasedAt: string | null;
  installedAt: string | null;
}

export interface CustomerAssetServiceEntry {
  id: string;
  number: number;
  summary: string;
  status: string;
  dateEntered: string | null;
  closedDate: string | null;
}

export interface CustomerAssetDetail extends CustomerAsset {
  siteName: string | null;
  contactName: string | null;
  notes: string | null;
  serviceTimeline: CustomerAssetServiceEntry[];
}

export interface CustomerAppointment {
  id: string;
  portalId: string | null;
  source: 'cw' | 'weclapp' | 'portal';
  title: string;
  type: string | null;
  technicianName: string | null;
  startAt: string | null;
  endAt: string | null;
  location: string | null;
  // Nur bei source='portal'
  status: string | null;
  notes: string | null;
  cwTicketId: number | null;
  durationMin: number | null;
  tickets?: { id: string; weclappTicketId: string | null; ticketNumber: string | null; subject: string | null }[];
}

export interface CustomerTeamContact {
  name: string;
  role: string;
  email: string;
  phone?: string;
  whatsapp?: string;
  appointmentUrl?: string;
  avatarInitials?: string;
  /** Profilfoto (HTTPS), aus Admin-Konfiguration – Phase 2.8 */
  photoUrl?: string;
}

export interface CustomerInboxMessage {
  id: string;
  title: string;
  body: string;
  relatedType: string | null;
  relatedId: string | null;
  readAt: string | null;
  createdAt: string;
}

// ─── HTTP-Hilfsfunktion ───────────────────────────────────────────────────────

function readCustomerCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)customer_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * 401 heißt nur dann „Du wurdest abgemeldet", wenn es eine Sitzung gab. Ohne gespeicherten
 * Kontakt war niemand angemeldet (erster Besuch, Link aus einer Mail) — dann schickt die
 * Anmeldeprüfung in CustomerPortalApp ohne Hinweis zur Anmeldeseite, samt `?next=`.
 * Bis v1.369.0 stand dort bei jedem ersten Besuch „Du wurdest abgemeldet".
 */
function signalCustomerSessionInvalid() {
  if (!useCustomerStore.getState().contact) return;
  window.dispatchEvent(new CustomEvent('customer-portal:session-invalid'));
}

async function customerRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (options.body != null && method !== 'GET' && method !== 'HEAD') {
    headers['Content-Type'] = 'application/json';
  }
  const csrf = readCustomerCsrfCookie();
  if (csrf && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    headers['X-CSRF-Token'] = csrf;
  }

  let res: Response;
  try {
    res = await fetch(endpoint, { ...options, headers, credentials: 'include' });
  } catch (err) {
    // Netzfehler kenntlich machen (isServerUnavailableError): Die Anmeldeprüfung wartet dann,
    // statt zur Anmeldeseite zu schicken (CustomerPortalApp).
    throw markNetworkError(err);
  }
  const raw = await res.text();

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(raw) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch { /* ignore */ }
    if (res.status === 401) signalCustomerSessionInvalid();
    if (res.status === 429) window.dispatchEvent(new CustomEvent('customer-portal:rate-limited'));
    throw Object.assign(new Error(message), { status: res.status });
  }

  if (res.status === 204 || raw.length === 0) return {} as T;
  return JSON.parse(raw) as T;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export async function requestCustomerMagicLink(email: string): Promise<void> {
  await customerRequest<{ message: string }>('/public/customer/login', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export interface CustomerSupportInfo {
  phone: string | null;
  /** Reine Ziffern inkl. Ländervorwahl (wa.me-Format) */
  whatsapp: string | null;
  email: string | null;
  hours: string | null;
  emergencyNote: string | null;
}

export interface CustomerSsoAvailability {
  microsoft: boolean;
  google: boolean;
  support?: CustomerSupportInfo;
}

/** Welche SSO-Provider fürs Kundenportal konfiguriert sind (Login-Seite). */
export async function getCustomerSsoConfig(): Promise<CustomerSsoAvailability> {
  return customerRequest<CustomerSsoAvailability>('/public/customer/sso/config');
}

/** Selbstauskunft (Firma/Name) zur eigenen SSO-Zugangsanfrage nachreichen. */
export async function submitCustomerSsoRequestDetails(input: {
  token: string;
  companyName: string;
  personName: string;
}): Promise<void> {
  await customerRequest('/public/customer/sso/access-request-details', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getCustomerMe(): Promise<CustomerContact> {
  return customerRequest<CustomerContact>('/api/customer/me');
}

export async function logoutCustomer(): Promise<void> {
  await customerRequest('/api/customer/logout', { method: 'POST' });
}

export async function getCustomerSessions(): Promise<CustomerSession[]> {
  return customerRequest<CustomerSession[]>('/api/customer/sessions');
}

export async function revokeCustomerSession(sessionId: string): Promise<void> {
  await customerRequest(`/api/customer/sessions/${sessionId}`, { method: 'DELETE' });
}

// ─── Tickets ──────────────────────────────────────────────────────────────────

export type TicketSortField = 'number' | 'title' | 'updated' | 'priority' | 'created' | 'deadline';

export async function getCustomerTickets(params?: {
  page?: number;
  pageSize?: number;
  status?: 'open' | 'closed' | 'all';
  search?: string;
  siteId?: number;
  sort?: TicketSortField;
  sortDir?: 'asc' | 'desc';
}): Promise<{
  tickets: CustomerTicket[];
  totalCount: number;
  page: number;
  pageSize: number;
}> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params?.status) qs.set('status', params.status);
  if (params?.search) qs.set('search', params.search);
  if (params?.siteId != null) qs.set('siteId', String(params.siteId));
  if (params?.sort) {
    qs.set('sort', params.sort);
    qs.set('sortDir', params.sortDir ?? 'desc');
  }
  return customerRequest(`/api/customer/tickets?${qs.toString()}`);
}

export async function getCustomerTicketDetail(id: string): Promise<CustomerTicketDetail> {
  return customerRequest(`/api/customer/tickets/${id}`);
}

export async function getCustomerTicketAttachments(id: string): Promise<{
  attachments: CustomerTicketAttachmentMeta[];
}> {
  return customerRequest(`/api/customer/tickets/${id}/attachments`);
}

export interface CustomerTicketComment {
  id: string;
  text: string;
  createdAt: string | null;
  author: string | null;
  /** true = vom Kunden über das Portal verfasst */
  fromCustomer: boolean;
  isResolution: boolean;
  /** true: `text` ist HTML. Sonst Klartext — die Anzeige soll das nicht raten müssen. */
  isHtml?: boolean;
}

export async function getCustomerTicketComments(id: string): Promise<{
  comments: CustomerTicketComment[];
}> {
  return customerRequest(`/api/customer/tickets/${id}/comments`);
}

export async function getCustomerTeamContacts(): Promise<{ contacts: CustomerTeamContact[] }> {
  return customerRequest('/api/customer/team-contacts');
}

// ─── Serviceberichte ──────────────────────────────────────────────────────────

export async function getCustomerSubmissions(params?: {
  page?: number;
}): Promise<{ submissions: CustomerSubmission[]; totalCount: number; page: number; pageSize: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  return customerRequest(`/api/customer/submissions?${qs.toString()}`);
}

export function getSubmissionDownloadUrl(id: string): string {
  return `/api/customer/submissions/${id}/download`;
}

// ─── Assets ───────────────────────────────────────────────────────────────────

export async function getCustomerAssets(params?: {
  search?: string;
  siteId?: number;
}): Promise<{ assets: CustomerAsset[] }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.siteId != null) qs.set('siteId', String(params.siteId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return customerRequest(`/api/customer/assets${suffix}`);
}

export async function getCustomerAsset(id: string): Promise<{ asset: CustomerAssetDetail }> {
  return customerRequest(`/api/customer/assets/${encodeURIComponent(id)}`);
}

// ─── Fristen (Lizenzen, Server, Domains, Verträge) ────────────────────────────

export type CustomerPortalDeadline = CustomerDeadlineBase;

/** Posten ohne Laufzeitende: Managed Service oder Vertrag mit angefragter Laufzeit — ohne Frist und Ampel. */
export interface CustomerPortalService {
  id: string;
  customerId: string;
  /** Gesellschaft des Postens (Konzern) */
  companyName: string | null;
  category: CustomerDeadlineCategory;
  ownership: CustomerDeadlineOwnership;
  name: string;
  provider: string | null;
  reference: string | null;
  startDate: string | null;
  customerNote: string | null;
  /** SERVICE_CONTRACT = Managed Service; PENDING = Laufzeit angefragt */
  termType: 'SERVICE_CONTRACT' | 'PENDING';
  /** Bei angefragter Laufzeit oft schon bekannt */
  autoRenew: boolean;
  renewalMonths: number | null;
  noticeDays: number | null;
  noticeAnchor: DeadlineNoticeAnchor;
  proofs: DeadlineProof[];
}

/** Freigegebener Beleg eines Postens — Anmeldung per Cookie, ein <img src> genügt. */
export function customerDeadlineProofUrl(deadlineId: string, proofId: string): string {
  return `/api/customer/deadlines/${encodeURIComponent(deadlineId)}/proofs/${encodeURIComponent(proofId)}`;
}

export async function getCustomerDeadlines(scope: 'single' | 'all' = 'single'): Promise<{
  deadlines: CustomerPortalDeadline[];
  /** Managed Services ohne eigene Laufzeit — getrennt, weil sie keine Frist haben */
  services: CustomerPortalService[];
  /** Verträge, deren Laufzeit angefragt ist — mit Hinweis, noch ohne Frist */
  pendingTerms: CustomerPortalService[];
  counts: Record<CustomerDeadlineStatus, number>;
  total: number;
  /** true, wenn mind. ein E-Mail-Empfänger (Opt-in-Kontakt oder Zusatz-Adresse) hinterlegt ist */
  mailRecipientsConfigured: boolean;
  scope: 'single' | 'all';
  /** Gesellschaften des Konzerns, bei denen dieser Bereich freigegeben ist */
  companies: Array<{ id: string; name: string; isHome: boolean }>;
}> {
  return customerRequest(scope === 'all' ? '/api/customer/deadlines?scope=all' : '/api/customer/deadlines');
}

export interface DeadlineNotificationSettings {
  contacts: { id: string; name: string; email: string | null; optIn: boolean }[];
  extraEmails: string[];
}

export async function getDeadlineNotificationSettings(): Promise<DeadlineNotificationSettings> {
  return customerRequest('/api/customer/deadlines/notification-settings');
}

export async function updateDeadlineNotificationSettings(body: {
  contactOptIns?: Record<string, boolean>;
  extraEmails?: string[];
}): Promise<{ ok: boolean }> {
  return customerRequest('/api/customer/deadlines/notification-settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Persönlicher Kalender-Abo-Link (Termine oder Fristen). Er gehört der angemeldeten Person und
 * endet mit ihrem Portal-Zugang. `legacyCompanyLink`: Die Firma hat noch den früheren gemeinsamen
 * Link — wer darüber abonniert hat, sollte neu abonnieren.
 */
export interface CalendarSubscriptionLink {
  token: string;
  feedUrl: string;
  legacyCompanyLink: boolean;
}

export async function getDeadlineCalendarSubscription(): Promise<CalendarSubscriptionLink> {
  return customerRequest('/api/customer/deadlines/calendar-subscription');
}

/** Widerruft nur das eigene Fristen-Abo. */
export async function revokeDeadlineCalendarSubscription(): Promise<{ ok: boolean }> {
  return customerRequest('/api/customer/deadlines/calendar-subscription', { method: 'DELETE' });
}

export interface OwnDeadlineInput {
  category?: import('./customerDeadlines').CustomerDeadlineCategory;
  name?: string;
  provider?: string;
  reference?: string;
  startDate?: string | null;
  endDate?: string;
  autoRenew?: boolean;
  renewalMonths?: number | null;
  noticeDays?: number | null;
  noticeAnchor?: DeadlineNoticeAnchor;
  customerNote?: string;
}

export async function createOwnDeadline(body: OwnDeadlineInput & { name: string; endDate: string }): Promise<{ id: string }> {
  return customerRequest('/api/customer/deadlines', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateOwnDeadline(id: string, body: OwnDeadlineInput): Promise<{ ok: boolean }> {
  return customerRequest(`/api/customer/deadlines/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function deleteOwnDeadline(id: string): Promise<{ ok: boolean }> {
  return customerRequest(`/api/customer/deadlines/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function setDeadlineRequestRef(
  id: string,
  body: { kind: 'CHANGE' | 'CANCEL' | 'RENEW' | 'QUESTION'; ticketId: string; ticketNumber?: string }
): Promise<{ ok: boolean }> {
  return customerRequest(`/api/customer/deadlines/${encodeURIComponent(id)}/request-ref`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─── Projekte (Bereich „Projekte", backend/src/routes/customerProjects.ts) ─────

/** Was der Kunde von einem Projekt sieht — bewusst ohne Aufgaben, Umfang und Aufwand. */
export interface CustomerProject {
  id: string;
  number: string;
  title: string;
  status: 'PLANNED' | 'IN_PROGRESS' | 'ON_HOLD' | 'DONE' | 'CANCELLED';
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  completedAt: string | null;
  updatedAt: string;
  progressPercent: number | null;
  /** Projektleitung bei FixByte */
  contactName: string | null;
  timeline: TimelineEntry[];
}

export async function getCustomerProjects(): Promise<{ projects: CustomerProject[] }> {
  return customerRequest('/api/customer/projects');
}

export async function getCustomerProject(id: string): Promise<{ project: CustomerProject }> {
  return customerRequest(`/api/customer/projects/${encodeURIComponent(id)}`);
}

// ─── Termine ──────────────────────────────────────────────────────────────────

export async function getCustomerAppointments(params?: {
  siteId?: number;
}): Promise<{ appointments: CustomerAppointment[] }> {
  const qs = new URLSearchParams();
  if (params?.siteId != null) qs.set('siteId', String(params.siteId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return customerRequest(`/api/customer/appointments${suffix}`);
}

export function getAppointmentIcsUrl(id: string): string {
  return `/api/customer/appointments/${id}/ics`;
}

export async function getOrCreateCalendarToken(): Promise<CalendarSubscriptionLink> {
  return customerRequest('/api/customer/calendar-token');
}

/** Widerruft nur das eigene Termine-Abo. */
export async function revokeCalendarToken(): Promise<{ ok: boolean }> {
  return customerRequest('/api/customer/calendar-token', { method: 'DELETE' });
}

export async function respondToPortalAppointment(
  id: string,
  body: { accept: boolean; note?: string }
): Promise<{ appointment: unknown }> {
  return customerRequest(`/api/customer/appointments/portal/${id}/respond`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─── In-App-Inbox ─────────────────────────────────────────────────────────────

export async function getCustomerInbox(params?: {
  page?: number;
  unreadOnly?: boolean;
}): Promise<{ messages: CustomerInboxMessage[]; unreadCount: number; page: number; pageSize: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.unreadOnly) qs.set('unreadOnly', '1');
  return customerRequest(`/api/customer/inbox?${qs.toString()}`);
}

export async function markInboxMessageRead(id: string): Promise<void> {
  await customerRequest(`/api/customer/inbox/${id}/read`, { method: 'POST' });
}

// ─── Onboarding ───────────────────────────────────────────────────────────────

export async function completeCustomerTour(): Promise<void> {
  await customerRequest('/api/customer/onboarding', {
    method: 'PATCH',
    body: JSON.stringify({ tourCompleted: true }),
  });
}

export async function updateChecklistItem(item: string, value: boolean): Promise<void> {
  await customerRequest('/api/customer/onboarding', {
    method: 'PATCH',
    body: JSON.stringify({ checklistItem: item, checklistValue: value }),
  });
}

export async function updateOnboardingVariant(variant: 'NEW_CUSTOMER' | 'EXISTING_CUSTOMER'): Promise<void> {
  await customerRequest('/api/customer/onboarding', {
    method: 'PATCH',
    body: JSON.stringify({ variant }),
  });
}

/** Phase 7.1: generisches Portal-Feedback (Smiley-Rail) */
export async function postCustomerPortalFeedback(body: {
  score: number;
  comment?: string;
}): Promise<void> {
  await customerRequest('/api/customer/feedback/portal', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Phase 7.1: prüft, ob der Kontakt bereits Portal-Feedback abgegeben hat */
export async function getCustomerPortalFeedbackStatus(): Promise<{ alreadySubmitted: boolean; show?: boolean }> {
  return customerRequest('/api/customer/feedback/portal/status');
}

// ─── Phase 3: Step-Up, Serviceanfrage, Kommentare, Notfall ───────────────────

export async function requestCustomerStepUp(purpose: string): Promise<void> {
  await customerRequest('/api/customer/step-up/request', {
    method: 'POST',
    body: JSON.stringify({ purpose }),
  });
}

export async function verifyCustomerStepUp(purpose: string, code: string): Promise<void> {
  await customerRequest('/api/customer/step-up/verify', {
    method: 'POST',
    body: JSON.stringify({ purpose, code }),
  });
}

export async function getCustomerStepUpStatus(): Promise<{ active: boolean; purpose?: string }> {
  return customerRequest('/api/customer/step-up/status');
}

export async function createCustomerServiceRequest(body: {
  summary: string;
  description: string;
  deadline?: string;
  siteId?: number;
  category?: 'SERVICE' | 'WARRANTY' | 'ORDER';
  claimType?: 'WARRANTY_CLAIM' | 'COMPLAINT';
  assetId?: string;
  assetName?: string;
  purchaseDate?: string;
  invoiceNumber?: string;
  /** Anfrage aus „Verträge & Lizenzen" — dürfen alle stellen, die den Bereich sehen (auch Gäste) */
  source?: 'contracts';
}): Promise<{
  ok: boolean;
  ticketId: string;
  ticketLink?: string | null;
  ticketNumber?: string;
  source: 'cw' | 'weclapp';
}> {
  return customerRequest('/api/customer/service-requests', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function submitContactSuggestion(body: {
  name: string;
  email: string;
  phone?: string;
  note?: string;
}): Promise<{ suggestion: { id: string; name: string; email: string } }> {
  return customerRequest('/api/customer/contact-suggestions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function postCustomerTicketComment(
  ticketId: string,
  text: string,
  authorName?: string,
  /** Formatierte Fassung inkl. eingebetteter Bilder (optional). */
  html?: string
): Promise<void> {
  await customerRequest(`/api/customer/tickets/${ticketId}/comments`, {
    method: 'POST',
    body: JSON.stringify({
      text,
      ...(authorName ? { authorName } : {}),
      ...(html?.trim() ? { html } : {}),
    }),
  });
}

export async function setCustomerTicketDeadline(ticketId: string, deadline: string | null): Promise<void> {
  await customerRequest(`/api/customer/tickets/${ticketId}/deadline`, {
    method: 'PATCH',
    body: JSON.stringify({ deadline }),
  });
}

/**
 * Ticket-Titel ändern. `authorName` ist Pflicht für Firmen-Sessions ohne
 * zugeordneten Kontakt (wie bei Kommentaren) — der alte Titel wird serverseitig
 * als sichtbarer Verlaufs-Kommentar protokolliert.
 */
export async function updateCustomerTicketTitle(
  ticketId: string,
  title: string,
  authorName?: string
): Promise<void> {
  await customerRequest(`/api/customer/tickets/${ticketId}/title`, {
    method: 'PATCH',
    body: JSON.stringify({ title, ...(authorName ? { authorName } : {}) }),
  });
}

export async function postCustomerTicketAttachment(
  ticketId: string,
  payload: { base64: string; filename: string; mimeType: string }
): Promise<void> {
  await customerRequest(`/api/customer/tickets/${ticketId}/attachments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Pre-Flight-Prüfung (Größe + Virenscan) vor dem eigentlichen Upload. */
export async function validateCustomerUpload(base64: string): Promise<void> {
  await customerRequest('/api/customer/uploads/validate', {
    method: 'POST',
    body: JSON.stringify({ base64 }),
  });
}

/**
 * Ticket wieder öffnen (Stufe B): hinterlegt eine Notiz und setzt den Status
 * provider-übergreifend zurück auf „In Bearbeitung" (CW + Weclapp).
 */
export async function reopenCustomerTicket(ticketId: string, note?: string): Promise<void> {
  await customerRequest(`/api/customer/tickets/${ticketId}/reopen`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  });
}

/**
 * Ticket vom Kunden schließen: hinterlegt optional einen Grund als Kommentar
 * und setzt den Status provider-übergreifend auf „Geschlossen".
 */
export async function closeCustomerTicket(ticketId: string, reason: string): Promise<void> {
  await customerRequest(`/api/customer/tickets/${ticketId}/close`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function postCustomerEmergency(description: string): Promise<{
  ok: boolean;
  ticketId: string;
  /** Ticketnummer (T26-… oder, solange weclapp das Ticket noch nicht hat, S26-…) */
  ticketNumber?: string | null;
  ticketLink?: string | null;
}> {
  return customerRequest('/api/customer/emergency', {
    method: 'POST',
    body: JSON.stringify({ description }),
  });
}

// ─── Phase 4: Status, Health, Trust, Audit, Export, CSAT ───────────────────

export async function getPublicCustomerPortalStatus(): Promise<{
  notices: PortalNotice[];
  degraded: boolean;
  message: string | null;
  updatedAt: string;
}> {
  const res = await fetch('/public/customer-portal/status');
  const raw = await res.text();
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(raw) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return JSON.parse(raw) as {
    notices: PortalNotice[];
    degraded: boolean;
    message: string | null;
    updatedAt: string;
  };
}

export type CustomerHealthLevel = 'green' | 'yellow' | 'red';

export interface CustomerHealthPayload {
  level: CustomerHealthLevel;
  reasons: string[];
  detail: { openTicketCount: number };
}

export async function getCustomerHealth(): Promise<CustomerHealthPayload> {
  return customerRequest('/api/customer/health');
}

export interface CustomerTrustCenterPayload {
  quarterLabel: string;
  slaPercent: number;
  trend12m: number[];
  footnote?: string;
}

export async function getCustomerTrustCenter(): Promise<CustomerTrustCenterPayload> {
  return customerRequest('/api/customer/trust');
}

export interface CustomerAuditLogRow {
  id: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  category: string;
  result: string;
  createdAt: string;
  /** Wer es getan hat: Name des Portal-Kontakts, „FixByte" für Aktionen von FixByte, sonst null */
  actorName: string | null;
}

export async function getCustomerAuditLog(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  items: CustomerAuditLogRow[];
  total: number;
  page: number;
  pageSize: number;
}> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return customerRequest(`/api/customer/audit-log${suffix}`);
}

export async function downloadCustomerDataExport(format: 'csv' | 'json'): Promise<void> {
  const res = await fetch(`/api/customer/export?format=${format}`, { credentials: 'include' });
  if (!res.ok) {
    const raw = await res.text();
    let message = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(raw) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      /* ignore */
    }
    if (res.status === 401) signalCustomerSessionInvalid();
    if (res.status === 429) window.dispatchEvent(new CustomEvent('customer-portal:rate-limited'));
    throw Object.assign(new Error(message), { status: res.status });
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  let filename = `fixbyte-portal-export.${format}`;
  const m = cd?.match(/filename="([^"]+)"/);
  if (m?.[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function getCustomerCsatStatus(ticketId: string): Promise<{
  ticketClosed: boolean;
  alreadySubmitted: boolean;
  canSubmit: boolean;
}> {
  return customerRequest(`/api/customer/feedback/csat/status/${encodeURIComponent(ticketId)}`);
}

export async function postCustomerCsat(body: {
  ticketId: string;
  score: number;
  comment?: string;
}): Promise<void> {
  await customerRequest('/api/customer/feedback/csat', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ─── Phase 5: Journey, NPS, Reports ───────────────────────────────────────────

export interface CustomerPhase5MetricsPayload {
  openTickets: number;
  closedInPeriod: number;
  ticketsSampled: number;
  totalCountReported: number;
  periodDays: number;
  source: 'cw' | 'weclapp' | 'none';
}

export interface CustomerPhase5InsightsPayload {
  avgResolutionHours: number | null;
  sampleSizeForAvg: number;
  slaPercent: number | null;
  slaQuarterLabel: string;
  outlook: string;
  topAssets: string[];
  avgResolutionNote: string;
}

export interface CustomerPhase5Status {
  onboarding: {
    variant: 'NEW_CUSTOMER' | 'EXISTING_CUSTOMER';
    startedAt: string | null;
    daysSinceStart: number;
    flags: { day14Sent: boolean; day30Sent: boolean; day90Sent: boolean } | null;
  };
  prompts: {
    day14: boolean;
    day30Widget: boolean;
    /** Phase 5.6: Jahresreport-Kachel (Bestandskunden oder Neukunden ab Tag 30) */
    annualReportWidget: boolean;
    day90Nps: boolean;
    quarterNps: boolean;
  };
  checklist: {
    milestoneOneYearDismissed: boolean;
    milestoneHundredTicketsDismissed: boolean;
  };
  milestones: {
    oneYear: { show: boolean; eligible: boolean };
    hundredTickets: { show: boolean; totalTicketsApprox: number; eligible: boolean };
  };
  metrics: { snapshot30d: CustomerPhase5MetricsPayload; annual365d: CustomerPhase5MetricsPayload };
  insights: { snapshot30d: CustomerPhase5InsightsPayload; annual365d: CustomerPhase5InsightsPayload };
  nps: { quarterReference: string; quarterSubmitted: boolean; onboarding90Submitted: boolean };
}

export async function getCustomerPhase5Status(): Promise<CustomerPhase5Status> {
  return customerRequest('/api/customer/phase5/status');
}

export async function postCustomerPhase5Day14(body: { score: number; comment?: string }): Promise<void> {
  await customerRequest('/api/customer/phase5/feedback/day14', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function postCustomerPhase5Nps(body: {
  score: number;
  comment?: string;
  scope: 'quarter' | 'onboarding90';
}): Promise<void> {
  await customerRequest('/api/customer/phase5/feedback/nps', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Umfrage weggeklickt — der Server fragt frühestens am nächsten Kalendertag wieder. */
export async function dismissCustomerPrompt(
  prompt: 'quarterNps' | 'day14' | 'day90Nps' | 'portalFeedback'
): Promise<void> {
  await customerRequest(`/api/customer/prompts/${prompt}/dismiss`, { method: 'POST' });
}

export async function postCustomerPhase5MilestoneDismiss(kind: 'one_year' | 'hundred_tickets'): Promise<void> {
  await customerRequest('/api/customer/phase5/milestones/dismiss', {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
}

export function getCustomerPhase5SnapshotPdfUrl(periodDays = 30): string {
  const qs = new URLSearchParams({ periodDays: String(periodDays) });
  return `/api/customer/phase5/reports/snapshot.pdf?${qs.toString()}`;
}

export function getCustomerPhase5AnnualPdfUrl(): string {
  return '/api/customer/phase5/reports/annual.pdf';
}

// ─── Phase 8: Help/FAQ, Quotes, SLA ──────────────────────────────────────────

export interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  order: number;
}

export async function getCustomerFaqs(params?: {
  search?: string;
  category?: string;
}): Promise<{ items: FaqItem[]; total: number; categories: string[] }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.category) qs.set('category', params.category);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return customerRequest(`/api/customer/help-faqs${suffix}`);
}

export type CustomerQuoteStatus = 'DRAFT' | 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';

export interface CustomerQuoteItem {
  id: string;
  position: number;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  totalPrice: string;
}

export interface CustomerQuote {
  id: string;
  quoteNumber: string;
  title: string;
  description: string;
  status: CustomerQuoteStatus;
  totalAmount: string;
  currency: string;
  items: CustomerQuoteItem[];
  validFrom: string;
  validUntil: string;
  pdfUrl: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

export async function getCustomerQuotes(params?: {
  status?: CustomerQuoteStatus;
  page?: number;
  pageSize?: number;
}): Promise<{ quotes: CustomerQuote[]; total: number; page: number; pageSize: number }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return customerRequest(`/api/customer/quotes${suffix}`);
}

export async function getCustomerQuoteDetail(quoteId: string): Promise<CustomerQuote> {
  return customerRequest(`/api/customer/quotes/${quoteId}`);
}

export async function acceptCustomerQuote(quoteId: string): Promise<CustomerQuote> {
  return customerRequest(`/api/customer/quotes/${quoteId}/accept`, { method: 'POST', body: '{}' });
}

export async function rejectCustomerQuote(quoteId: string, reason?: string): Promise<CustomerQuote> {
  return customerRequest(`/api/customer/quotes/${quoteId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface CustomerSlaStatus {
  hasActiveSla: boolean;
  responseHours?: number;
  resolutionHours?: number;
  pauseOnWaiting?: boolean;
  agreementNumber?: string;
  firstSeenAt?: string | null;
  /** Vereinbarte Servicezeit im Klartext („Mo–Fr 08:00–17:00"). */
  serviceWindow?: string;
  /**
   * Fälligkeit, vom Server in Servicezeit gerechnet — nachts, am Wochenende und an
   * Feiertagen steht die Uhr. Null, wenn das Ticket noch nicht im Spiegel liegt.
   */
  responseDueAt?: string | null;
  resolutionDueAt?: string | null;
}

export async function getCustomerTicketSla(ticketId: string): Promise<CustomerSlaStatus> {
  return customerRequest(`/api/customer/tickets/${ticketId}/sla`);
}

// ─── Permissions ───────────────────────────────────────────────────────────

export type Permission =
  | 'contact:view_all' | 'contact:view_own' | 'contact:edit' | 'contact:add' | 'contact:remove'
  | 'howto:view' | 'howto:request_feature'
  | 'ticket:create' | 'ticket:view_all' | 'ticket:view_own' | 'ticket:comment' | 'ticket:close' | 'ticket:upload_attachment'
  | 'quote:view' | 'quote:accept' | 'quote:reject' | 'quote:request'
  | 'invoice:view' | 'invoice:pay' | 'invoice:comment' | 'invoice:dispute'
  | 'reminder:view' | 'reminder:acknowledge' | 'sla:view_terms' | 'sla:dispute_miss'
  | 'asset:view' | 'asset:request_change'
  | 'admin:manage_contacts' | 'admin:create_magic_links' | 'admin:view_audit_logs' | 'admin:revoke_sessions' | 'admin:change_settings';

export interface CustomerPermissionsResponse {
  permissions: Permission[];
  role: 'ADMIN' | 'MITARBEITER' | 'GAST';
}

export async function getCustomerPermissions(): Promise<CustomerPermissionsResponse> {
  return customerRequest('/api/customer/permissions');
}

// ─── Team-Verwaltung (Phase 3.10) ────────────────────────────────────────────

export type TeamMemberRole = 'ADMIN' | 'MITARBEITER' | 'GAST';

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: TeamMemberRole;
  createdAt: string;
  cwContactId: number | null;
  weclappPortalContactId: string | null;
}

export async function getCustomerTeam(): Promise<{ contacts: TeamMember[] }> {
  return customerRequest('/api/customer/team');
}

export async function inviteTeamMember(body: {
  email: string;
  name: string;
  role: TeamMemberRole;
}): Promise<{ contact: TeamMember }> {
  return customerRequest('/api/customer/team/invite', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function changeTeamMemberRole(id: string, role: TeamMemberRole): Promise<{ contact: TeamMember }> {
  return customerRequest(`/api/customer/team/${encodeURIComponent(id)}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export async function removeTeamMember(id: string): Promise<{ ok: boolean }> {
  return customerRequest(`/api/customer/team/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ─── Onboarding ────────────────────────────────────────────────────────────

export async function updateCustomerOnboarding(data: {
  checklistItem?: { id: string; completed: boolean };
  tourCompleted?: boolean;
  variant?: string;
}): Promise<{ tourCompletedAt?: string | null; checklistState?: Record<string, boolean>; variant?: string }> {
  const body: Record<string, unknown> = {};
  if (data.checklistItem) {
    body.checklistItem = data.checklistItem.id;
    body.checklistValue = data.checklistItem.completed;
  }
  if (data.tourCompleted !== undefined) {
    body.tourCompleted = data.tourCompleted;
  }
  if (data.variant) {
    body.variant = data.variant;
  }
  return customerRequest('/api/customer/onboarding', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// ─── Shop ──────────────────────────────────────────────────────────────────

export interface ShopItem {
  id: number;
  itemType: 'PRODUCT' | 'SERVICE';
  name: string;
  description: string | null;
  sku: string | null;
  listPriceCents: number;
  discountedPriceCents: number;
  hasDiscount: boolean;
  imageUrl: string | null;
}

export interface ShopCategory {
  id: number;
  name: string;
  /** NULL = Wurzelkategorie; sonst ID der übergeordneten Kategorie */
  parentId: number | null;
  /** 1 = Wurzelkategorie */
  depth: number;
  /** Namen von der Wurzel bis einschließlich dieser Kategorie */
  path: string[];
  /** Nur die direkt zugeordneten Artikel — Unterkategorien haben eigene Einträge */
  items: ShopItem[];
}

export interface ShopCatalogResponse {
  categories: ShopCategory[];
  discountPercent: number;
}

export async function getCustomerShopCatalog(): Promise<ShopCatalogResponse> {
  return customerRequest<ShopCatalogResponse>('/api/customer/shop/categories');
}

// ─── Mitarbeiter-Verzeichnis ────────────────────────────────────────────────

export interface CustomerEmployee {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  department: string | null;
  position: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerEmployeeInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  department?: string | null;
  position?: string | null;
  notes?: string | null;
}

export async function getCustomerEmployees(): Promise<{ employees: CustomerEmployee[] }> {
  return customerRequest('/api/customer/employees');
}

export async function createCustomerEmployee(data: CustomerEmployeeInput): Promise<{ employee: CustomerEmployee }> {
  return customerRequest('/api/customer/employees', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerEmployee(id: string, data: CustomerEmployeeInput): Promise<{ employee: CustomerEmployee }> {
  return customerRequest(`/api/customer/employees/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCustomerEmployee(id: string): Promise<{ ok: boolean }> {
  return customerRequest(`/api/customer/employees/${id}`, { method: 'DELETE' });
}

// ─── Serviceberichte (Monat / Quartal / Jahr) ─────────────────────────────────

export interface CustomerPortalReportListEntry {
  id: string;
  title: string;
  periodType: 'MONTH' | 'QUARTER' | 'YEAR';
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  releasedAt: string | null;
  teaser: string | null;
}

export interface CustomerPortalReportView {
  id: string;
  /**
   * Verträge & Lizenzen. Fehlt, wenn der Abschnitt ausgeschaltet ist oder der Betrachter
   * das Paket „Verträge & Lizenzen" nicht hat — dann blendet das Portal ihn einfach aus.
   */
  deadlines?: PortalReportDeadlines | null;
  title: string;
  periodType: 'MONTH' | 'QUARTER' | 'YEAR';
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  releasedAt: string | null;
  customerName: string;
  summary: string | null;
  metrics: {
    ticketsOpened: number;
    ticketsClosed: number;
    openAtPeriodEnd: number;
    hoursBooked: number;
    avgFirstResponseHours: number | null;
    avgFirstContactHours: number | null;
    avgResolutionHours: number | null;
    previous: {
      periodLabel: string;
      ticketsOpened: number;
      ticketsClosed: number;
      hoursBooked: number;
      calls: number;
      chats: number;
    } | null;
    sla: {
      responseHours: number;
      resolutionHours: number;
      responseEvaluated: number;
      resolutionEvaluated: number;
      responseMetPercent: number | null;
      resolutionMetPercent: number | null;
      agreementNumber: string | null;
    } | null;
    byPriority: { name: string; count: number }[];
    /** Telefonate und Chats des Zeitraums */
    channels: {
      calls: {
        total: number;
        inbound: number;
        outbound: number;
        inboundMissed: number;
        talkMinutes: number;
      };
      chats: {
        conversations: number;
        messagesIn: number;
        messagesOut: number;
        avgFirstReplyMinutes: number | null;
        channels: string[];
      };
    } | null;
    notes: string[];
  } | null;
  sections: {
    kind: 'CRITICAL' | 'POSITIVE' | 'IMPROVEMENT' | 'UPSELL';
    items: {
      id: string;
      title: string;
      body: string;
      ticketNumbers: string[];
      effortHint: string | null;
      valueHint: string | null;
    }[];
  }[];
  goals: {
    id: string;
    title: string;
    detail: string | null;
    owner: string;
    status: string;
    dueDate: string | null;
    carriedOver: boolean;
  }[];
  tickets: {
    id: string;
    ticketNumber: string | null;
    subject: string;
    status: string | null;
    priority: string | null;
    openedAt: string | null;
    closedAt: string | null;
    firstResponseMinutes: number | null;
    firstContactMinutes: number | null;
    firstContactSource: string | null;
    callCount: number;
    callMinutes: number;
    resolutionHours: number | null;
    slaResponseMet: boolean | null;
    slaResolutionMet: boolean | null;
    hoursBooked: number;
    preview: string | null;
    diagnosis: string | null;
  }[];
}

export async function getCustomerReports(): Promise<{ reports: CustomerPortalReportListEntry[] }> {
  return customerRequest('/api/customer/reports');
}

export async function getCustomerReport(id: string): Promise<{ view: CustomerPortalReportView }> {
  return customerRequest(`/api/customer/reports/${encodeURIComponent(id)}`);
}

/** PDF-Adresse — Cookie-Auth, daher direkt im Browser aufrufbar. */
export function customerReportPdfHref(id: string): string {
  return `/api/customer/reports/${encodeURIComponent(id)}/pdf`;
}


/** Gesellschaften des Konzerns, die dieser Ansprechpartner sehen darf. */
export async function getPortalCompanies(): Promise<{
  companies: Array<PortalCompany & { modules: PortalModuleId[] }>;
  activeCustomerId: string;
}> {
  return customerRequest('/api/customer/companies');
}

/** Gesellschaft wechseln. Danach muss /me neu geladen werden — Bereiche und Rechte ändern sich mit. */
export async function switchPortalCompany(customerId: string): Promise<{
  activeCustomerId: string;
  name: string;
  modules: PortalModuleId[];
}> {
  return customerRequest('/api/customer/companies/active', {
    method: 'POST',
    body: JSON.stringify({ customerId }),
  });
}


/** Verträge & Lizenzen im Service-Rückblick (eingefrorener Stand zum Periodenende) */
export interface PortalReportDeadlines {
  monitored: number;
  viaFixbyte: number;
  customerManaged: number;
  selfEntries: number;
  remindersSent: number;
  dueInPeriod: PortalReportDeadlineRow[];
  dueInPeriodTotal: number;
  upcoming: PortalReportDeadlineRow[];
  upcomingTotal: number;
  outlookDays: number;
}

export interface PortalReportDeadlineRow {
  name: string;
  category: string;
  provider: string | null;
  ownership: string;
  endDate: string;
  actionDate: string;
  actionIsCancellation: boolean;
  status: 'EXPIRED' | 'CRITICAL' | 'WARNING' | 'RENEWING' | 'ENDING' | 'OK';
  outcome: 'RENEWS' | 'ENDS' | 'ACTION';
}

// ── DNS-Zonen (v1.408.0) — nur lesen, nur bestätigte eigene Zonen, nur DNS-Records ────────────

export interface CustomerDnsRecord {
  type: string;
  name: string;
  content: string | null;
  ttl: number | null;
  proxied: boolean | null;
  priority: number | null;
  data: unknown;
}

export interface CustomerDnsZoneSummary {
  id: string;
  name: string;
  active: boolean;
  recordCount: number;
  lastCheckedAt: string | null;
  lastChangedAt: string | null;
}

export interface CustomerDnsZoneDetail {
  zone: { id: string; name: string; active: boolean; lastCheckedAt: string | null; lastChangedAt: string | null };
  records: CustomerDnsRecord[];
  history: Array<{
    at: string;
    initial: boolean;
    changes: {
      added: CustomerDnsRecord[];
      removed: CustomerDnsRecord[];
      changed: Array<{ type: string; name: string; fields: string[]; before: CustomerDnsRecord; after: CustomerDnsRecord }>;
    };
  }>;
}

export async function getCustomerDnsZones(): Promise<{ zones: CustomerDnsZoneSummary[] }> {
  return customerRequest('/api/customer/dns-zones');
}

export async function getCustomerDnsZone(id: string): Promise<CustomerDnsZoneDetail> {
  return customerRequest(`/api/customer/dns-zones/${encodeURIComponent(id)}`);
}
